<template>
  <div class="pending-actions" :class="mainElementClasses">
    <FiltersZone
      v-if="!asWidget"
      :force-open="forceOpenOptions"
      :disabled="forceOpenOptions"
      :modal-title="$t('message.OPTIONS')"
    >
      <div slot="shortcuts">
        <slot name="filters-shortcut" />
      </div>
      <ComponentsList :value="filteredPendOpt" @edit="paramsEdit" />
    </FiltersZone>
    <div class="pending-actions__main-container f-h-below-filters">
      <!-- Action list -->
      <div ref="pendingActions" class="f-h pending-list">
        <RefreshOnscrollList
          ref="refList"
          class="f-h"
          @scrollBottom="onScrollBottom"
        >
          <div ref="itemsHolder">
            <div v-if="!asWidget" class="pending-actions__col-headers v-centerer">
              <SortOption
                class="pending-actions__col-headers__action"
                :label="$t('message.ACTION')"
                :is-current="pendOptSortBy.value === 0"
                :is-desc="pendOptSortBy.value === 0 && pendOptSortBy.desc"
                sortable
                @click.native="clickedSortOption(0)"
              />
              <SortOption class="pending-actions__col-headers__tags" :label="$t('message.TAGS')" />
              <SortOption class="pending-actions__col-headers__leader" :label="$t('message.LEADER')" />
              <SortOption
                class="pending-actions__col-headers__impact"
                :label="$t('message.IMPACT')"
                :is-current="pendOptSortBy.value === 3"
                :is-desc="pendOptSortBy.value === 3 && pendOptSortBy.desc"
                sortable
                @click.native="clickedSortOption(3)"
              />
              <SortOption
                class="pending-actions__col-headers__deadline"
                :label="$t('message.DEADLINE')"
                :is-current="pendOptSortBy.value === 2"
                :is-desc="pendOptSortBy.value === 2 && pendOptSortBy.desc"
                sortable
                @click.native="clickedSortOption(2)"
              />
              <SortOption class="pending-actions__col-headers__delete" :label="$t('message.DELETE')" />
            </div>

            <div v-if="sortedOfflineActions.length > 0" class="pending-actions__offline-items">
              <h3>{{ $t('message.OFFLINE_ACTIONS') }}</h3>
              <PendingActionItem
                v-for="(action, index) in sortedOfflineActions"
                ref="actionItems"
                :key="action.id"
                :data-action-idx="index"
                :value="action"
                :no-drag-handle="isSmartphoneLandscape || asWidget || archives"
                @sheetClick="sheetClicked(action)"
                @actionClick="sheetClicked(action, true)"
                @deleteClick="deleteAction(action.id)"
              />
            </div>

            <PendingActionItem
              v-for="(action, index) in sortedActions"
              ref="actionItems"
              :key="action.id"
              :data-action-idx="index"
              :value="action"
              :no-drag-handle="isSmartphoneLandscape || asWidget || archives"
              @sheetClick="sheetClicked(action)"
              @actionClick="sheetClicked(action, true)"
              @deleteClick="deleteAction(action.id)"
            />
          </div>
        </RefreshOnscrollList>
      </div>
      <!-- Plan dropzones -->
      <div v-if="!asWidget && !archives" ref="pendingPlans" class="f-h pending-plans">
        <div
          v-for="(plan, index) in compPlans"
          :key="plan.id"
          :data-plan-idx="index"
          class="plan-item generic-border"
        >
          <div class="plan-item__title">
            {{ decodeHTML(plan.name) }}
          </div>
          <div class="plan-item__capacity">
            {{ fillIndicator(plan) }}
          </div>
          <!-- <img src="/images/drop-box.png" alt="Drop here"> -->
          <div class="plan-item__help">
            {{ $t("message.PLAN_DROP_HELP") }}
          </div>
        </div>
      </div>
    </div>

    <!-- Team picker when sending an action to a support plan -->
    <AdvancedTeamPicker
      v-if="supportData.plan != null"
      :value="[]"
      display-all-teams
      invisible
      ok-button
      single-value
      edit-mode-always-on
      @input="suppTeamClicked"
    />
  </div>
</template>

<script>
/**
 * List of pending actions.
 * Actions can be moved to an action plan thrugh drag and drop.
 *
 * @vue-prop {Boolean} archives - If true, fetch archived actions
 */

import $ from 'jquery';
import toastr from 'toastr';
// Vuex utils
import { mapState } from 'vuex';
import {
  ASSIGN_ACTION,
  RETURN_ACTION,
  FETCH_TEAMS,
  FETCH_USERS,
  FETCH_ACTION_PLANS,
} from '@/store/action-types';
import { GET_USERS, GET_TEAMS, GET_ACTION_PLANS } from '@/store/mutation-types';
import { FETCH_FILTERED_ACTION, DELETE_ACTION } from '@/store/modules/problem/action-types';
// Import components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import ComponentsList from '@/components/components-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import PendingActionItem from '@/components/pending-action-item.vue';
import RefreshOnscrollList from '@/components/refresh-onscroll-list.vue';
import SortOption from '@/components/sort-option.vue';
import mixinPendingOptions from '@/mixins/page-options/pending.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import IAHelpStep from '@/utils/classes/IAHelpStep';
import {
  capitalizeFirstLetter,
  calert,
  decodeHTML,
  itemWidthClasses,
} from '@/utils/main';
import { parseImpactFilterParam } from '@/utils/problem-utils';

const REQ_ITEMS_COUNT = 20;

export default {

  name: 'Pending',

  components: {
    AdvancedTeamPicker,
    ComponentsList,
    FiltersZone,
    PendingActionItem,
    RefreshOnscrollList,
    SortOption,
  },

  mixins: [mixinSocketHandler, mixinPendingOptions],

  props: {
    asWidget: Boolean,
    archives: Boolean,
    widgetImpactRange: {
      type: [Object, Array],
      default: () => ({ min: 0, max: 3 }),
    },
    widgetSortBy: {
      type: Object,
      default: () => ({ value: 3, desc: true }),
    },
    widgetLeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      elClasses: {},
      demoPlans: [],
      // Pending actions
      demoActions: [],
      supportItems: [
        { id: 0, label: this.$t('message.ALL') },
        { id: 1, label: this.$t('message.TEAM_ACTIONS') },
        { id: 2, label: this.$t('message.ACTIONS_RECEIVED') },
      ],
      itemsLimitOffset: 0,
      sqlLimit: REQ_ITEMS_COUNT,
      loadingActions: false,
      itemsPadding: 40,
      destroyed: false,
      preventActionClick: false,
      // Support assignment
      supportData: { plan: null, action: null },
      forceOpenOptions: false,
      // Properties used by other components. See src/views/readme.md
      pageTitle: capitalizeFirstLetter(this.$t('message.PENDING_ACTIONS')),
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('PLAN_ACTION'), this.teacherBtn('SECURING_ACTION')],
    };
  },

  computed: {
    /** Object: Classes applied to this.$el */
    mainElementClasses() {
      return {
        ...this.elClasses,
        'pending-actions--as-widget': this.asWidget,
        'pending-actions--archives': this.archives,
      };
    },
    sortItems() {
      return [
        { id: 0, label: this.$t('message.SORT_BY_TITLE') },
        { id: 1, label: this.$t('message.SORT_BY_ENTRY_DATE') },
        { id: 2, label: this.$t('message.SORT_BY_DEADLINE') },
        { id: 3, label: this.$t('message.SORT_BY_IMPACT') },
      ];
    },
    /** Array: Problems from the state */
    actions() {
      const fetchConfig = this.$store.state.problem.actionsFetchConfigurations[JSON.stringify(this.refreshParams)];
      const actionIds = fetchConfig ? fetchConfig.actionIds : [];
      return actionIds.map(id => this.$store.state.problem.actions[`${id}`]);
    },
    /** Array: Return the actions fetched from the server and sorted according to sorting settings */
    sortedActions() {
      const compActions = this.demoMode ? this.demoActions : this.actions;
      return this.sortActions(compActions);
    },
    /** Array: Return the offline actions created during offlineMode session */
    sortedOfflineActions() {
      if (!this.$store.state.device.offlineMode) {
        return [];
      }
      const actions = Object.values(this.$store.state.problem.actions)
        .filter(a => a.id < 0 && a.state === 0);
      return this.sortActions(actions);
    },
    compPlans() {
      return this.demoMode ? this.demoPlans : this.actionPlans;
    },
    /**
     * @vue-computed {Object} refreshParams - Parameters to set when requesting actions
     */
    refreshParams() {
      const params = {
        pending: !this.archives,
        archived: this.archives,
        sortBy: {
          index: this.pendOptSortBy.value,
          desc: this.pendOptSortBy.desc,
        },
        impact: parseImpactFilterParam(this.pendOptImpactRange),
      };
      // 'users' param
      if (this.pendOptLeader && this.pendOptLeader.length) {
        params.users = this.pendOptLeader.map(leader => Number(leader));
      }
      // 'filterSupport' param
      switch (this.filterSupport) {
        case 1:
          params.filterSupport = false;
          break;
        case 2:
          params.filterSupport = true;
          break;
        case 0:
        default:
          break;
      }
      return params;
    },
    // ========= Config options =========
    pendOptSortBy() {
      if (this.asWidget) {
        return this.widgetSortBy;
      }
      const sortBy = Number(this.$route.query.sortBy);
      return {
        value: Number.isNaN(sortBy) ? 1 : sortBy,
        desc: this.$route.query.sortDesc === 'true',
      };
    },
    pendOptLeader() {
      if (this.asWidget) {
        return this.widgetLeaders;
      }
      const queryIds = this.$route.query.leader;
      if (queryIds === '') {
        return [];
      }
      return typeof queryIds === 'undefined'
        ? this.pendOptUserItems.map(t => t.id)
        : queryIds.split(',').map(e => Number(e));
    },
    pendOptImpactRange() {
      if (this.asWidget) {
        return this.widgetImpactRange;
      }
      const vals = this.$route.query.impactRange;
      return !vals || vals === '' ? [0, 1, 2, 3] : vals.split(',').map(z => Number(z));
    },
    // 'support' query
    filterSupport: {
      get() {
        const support = Number(this.$route.query.support);
        return Number.isNaN(support) ? 0 : support;
      },
      set(newValue) {
        const query = Object.assign({}, this.$route.query, { support: newValue });
        this.$router.replace({ query });
      },
    },
    /** Array: Users available in action-item */
    leaderRadioItems() {
      return this.users.map(u => ({ ...u, radioId: `${this.currentTeam.id}-${u.id}` }));
    },
    /** Array: Return only params we need in the panel */
    filteredPendOpt() {
      const pendOpts = ['displayNumberOfPendingActionsTitle', 'displayNumberOfPendingActions'];
      return this.pendOptions.filter(option => !pendOpts.includes(option.key));
    },
    ...mapState({
      teamsLoading: state => state[GET_TEAMS.loadingKey],
      usersLoading: state => state[GET_USERS.loadingKey],
      users: state => state[GET_USERS.stateKey] || [],
      actionPlansLoading: state => state[GET_ACTION_PLANS.loadingKey],
      actionPlans: state => state[GET_ACTION_PLANS.stateKey] || [],
    }),
    ...mapState('device', {
      isSmartphoneLandscape: state => state.isSmartphoneLandscape,
    }),
  },

  watch: {
    $route() {
      this.refresh();
    },
    usersLoading(areLoading) {
      if (!areLoading) {
        this.refresh();
      }
    },
    isSmartphoneLandscape() {
      this.setupDragAndDrop();
    },
  },

  created() {
    if (!this.usersLoading) {
      this.$store.dispatch(FETCH_USERS);
    }
    if (!this.teamsLoading) {
      // Fetch teams for leader in pending action items
      this.$store.dispatch(FETCH_TEAMS);
    }
    if (!this.actionPlansLoading) {
      this.$store.dispatch(FETCH_ACTION_PLANS);
    }
    this.refresh();
    $(window).on(`resizeEnd.${this.uuid}`, () => {
      // Redraw charts
      this.setupDragAndDrop();
      this.onSizeChanged();
    });
    this.registerSocketHandlers();
  },

  mounted() {
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resizeEnd.${this.uuid}`);
    this.destroyed = true;
    this.destroyDragAndDrop();
  },

  methods: {
    capitalizeFirstLetter,
    decodeHTML,
    /**
     * Register all socket handlers
     */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.PENDING, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PENDING_AND_PLANS, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PENDING_AND_PROBLEMS, () => {
        this.refresh();
      });
    },
    /**
     * Event handler - Triggered when scrolled to bottom
     */
    onScrollBottom() {
      if (!this.loadingActions) {
        this.refresh(true);
      }
    },
    /** Sorting / Filters options updated */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'impactRange') {
        query.impactRange = option.value.join(',');
      }
      if (option.key === 'leader') {
        query.leader = option.value.join(',');
      }
      if (option.key === 'sortBy') {
        query.sortBy = option.value.value;
        query.sortDesc = String(option.value.desc);
      }
      this.$router.replace({ query });
    },
    sheetClicked(action, actionClicked) {
      if (actionClicked || Number(action.problem) === 0) {
        this.$router.push(this.getActionCreationLink(action.id));
      } else {
        this.$router.push(this.getPbSheetLink(action.problem));
      }
    },
    /** Returns displayed fill indicator for an action plan */
    fillIndicator(plan) {
      return `(${plan.nActions} / ${Number(plan.support) === 1 ? '∞' : plan.maxActions})`;
    },
    /** Ask user confirmation before adding an action to a plan (with this.Assign) */
    promptAssign(action, plan) {
      if (action && plan) {
        // If action is a securing action, cannot be assigned to an action plan but is just set as 'ongoing' instead
        if (action.isSecuring || action.isQuickAction) {
          calert(this.$t('message.ASSIGN_TO_PLAN_IS_QUICK', { actionName: action.name, planName: plan.name }), [
            {
              title: this.$t('message.SET_ACTION_ONGOING'),
              action: () => { this.assign(action, plan); },
              sClass: 'green',
            },
            {
              title: this.$t('message.CANCEL'),
            },
          ]);
        } else {
          calert(this.$t('message.ASSIGN_TO_PLAN_CONFIRM', { actionName: action.name, planName: plan.name }), [
            {
              title: this.$t('message.ASSIGN_ACTION'),
              action: () => { this.assign(action, plan); },
              sClass: 'green',
            },
            {
              title: this.$t('message.CANCEL'),
            },
          ]);
        }
      }
    },
    /** Put an action in a plan */
    assign(action, plan) {
      // Support action plan => assign to another team
      if (Number(plan.support) === 1) {
        this.supportData = { plan, action };
        return;
      }
      this.$store.dispatch(ASSIGN_ACTION, { action, planId: plan.id }).then(this.refresh);
    },
    /** Send request to assign the action to the selected team */
    suppTeamClicked(teams) {
      if (teams == null || teams.length === 0) {
        this.supportData.plan = null;
        return;
      }
      const params = {
        action: this.supportData.action,
        planId: this.supportData.plan.id,
        fromPlanId: 0,
        supportTeamId: teams[0],
      };
      this.$store.dispatch(ASSIGN_ACTION, params).then(this.refresh);
      this.supportData.plan = null;
    },
    /** Completely remove an action from the database */
    deleteAction(id) {
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE_ACTION'),
          action: () => { this.confirmDeleteAction(id); },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    confirmDeleteAction(id) {
      this.$store.dispatch(`problem/${DELETE_ACTION}`, { id }).then(({ data }) => {
        if (data === 1) {
          this.sendSocketMessage(SocketMessages.CC_MSGS.PENDING);
          toastr.success(this.$t('message.ACTION_DELETED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
        this.refresh();
      });
    },
    /** Allow the user to return an action */
    promptReturnAction(action) {
      calert(`${this.$t('message.ACTION_FROM_OTHER_TEAM')}<br/>${this.$t('message.CANT_DELETE_BUT_RETURN')}`, [
        { title: this.$t('message.RETURN_ACTION'), action: () => { this.returnAction(action); } },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** If the action was assigned from another team, return the action to the previous team */
    returnAction(action) {
      this.$store.dispatch(RETURN_ACTION, { id: action.id, team: action.fromTeam }).then(this.refresh);
    },
    /**
     * Fetch actions from server
     * @param {Boolean} increaseLimit - If false, will reload actions from the top
     */
    refresh(increaseLimit) {
      this.$store.dispatch(FETCH_ACTION_PLANS);
      this.loadingActions = true;
      const params = { ...this.refreshParams };
      params.limit = { offset: this.itemsLimitOffset, count: this.sqlLimit };
      if (increaseLimit !== true) {
        params.limit.count = this.itemsLimitOffset || REQ_ITEMS_COUNT;
        params.limit.offset = 0;
      }

      // Refresh the data
      this.$store.dispatch(`problem/${FETCH_FILTERED_ACTION}`, params).then((data) => {
        this.itemsLimitOffset = this.actions.length;
        this.sqlLimit = REQ_ITEMS_COUNT;
        this.loadingActions = false;
        // Setting up drag & drop
        this.$nextTick(() => {
          // refresh size checks in case scroll bar appeared/disappeared
          this.onSizeChanged();
          this.setupDragAndDrop();
          if (data.length > 0 && this.$refs.refList) {
            setTimeout(this.$refs.refList.checkHeight, 500);
          }
        });
      });
    },
    /** Destroy draggable and droppable instances */
    destroyDragAndDrop() {
      const dropInstance = $('.pending-plans .plan-item').droppable('instance');
      if (dropInstance) {
        dropInstance.destroy();
      }
    },
    /** Allow dragging actions to action plans directly */
    setupDragAndDrop() {
      this.destroyDragAndDrop();
      if (this.destroyed || this.isSmartphoneLandscape || this.asWidget) {
        return;
      }
      // Drag to a plan
      // Large screen plans
      $('.pending-plans .plan-item').droppable({
        addClasses: false,
        accept: '.pending-action-item',
        tolerance: 'pointer',
        drop: (e, ui) => {
          const aIndex = Number(ui.draggable.data('actionIdx'));
          const pIndex = Number(e.target.dataset.planIdx);
          this.promptAssign(this.sortedActions[aIndex], this.actionPlans[pIndex]);
        },
      });
    },
    /** Style of the problem is determined by the size of $el. Apply classes */
    onSizeChanged(callOnChildren) {
      const { itemsHolder, actionItems } = this.$refs;
      const elt = (actionItems && actionItems[0] && actionItems[0].$el) || itemsHolder || {};
      const width = elt.offsetWidth;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});

      if (callOnChildren) {
        actionItems.forEach((comp) => {
          if (comp && comp.onSizeChanged) {
            comp.onSizeChanged();
          }
        });
      }
    },
    /**
     * Sort a given array of actions according to sorting settings
     * @param  {Array} actions
     * @return {Array}  Sorted array
     */
    sortActions(actions) {
      const { desc } = this.pendOptSortBy;
      switch (this.pendOptSortBy.value) {
        case 0:
          return !desc
            ? actions.sort((a, b) => a.name.localeCompare(b.name))
            : actions.sort((a, b) => b.name.localeCompare(a.name));
        case 1:
          return !desc
            ? actions.sort((a, b) => a.createdSortable - b.createdSortable)
            : actions.sort((a, b) => b.createdSortable - a.createdSortable);
        case 2:
          return !desc
            ? actions.sort((a, b) => a.deadlineSortable - b.deadlineSortable)
            : actions.sort((a, b) => b.deadlineSortable - a.deadlineSortable);
        case 3:
          return !desc
            ? actions.sort((a, b) => a.impact - b.impact)
            : actions.sort((a, b) => b.impact - a.impact);
        default:
          return actions;
      }
    },
    userLoaded() {
      // Refresh necesary data for pending filters
      this.pendOptUserLoaded();
    },
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.PEND_INTRO')),
        new IAHelpStep('.pending-action-item', this.$t('help.PEND_ITEM')),
        new IAHelpStep('.pending-action-item .pending-action-item__basics', this.$t('help.PEND_DESCRIPTION')),
        new IAHelpStep('.pending-action-item .pending-action-item__basics__deadline', this.$t('help.PEND_DEADLINE')),
        new IAHelpStep('.pending-action-item .expand', this.$t('help.PEND_EXPAND'), () => {
          $('.pending-action-item .expand').trigger('click');
        }),
        new IAHelpStep('.pending-action-item .see-sheet', this.$t('help.PEND_SEE_SHEET'), () => {
          $('.pending-action-item .expand').trigger('click');
        }),
        new IAHelpStep('.pending-action-item__basics__secu', this.$t('help.PEND_SECURING'), null, [
          this.teacherBtn('SECURING_ACTION'),
        ]),
        new IAHelpStep('.filters-zone', this.$t('help.PEND_SORT'), (e, next) => {
          this.forceOpenOptions = true;
          this.$nextTick(next);
          return true;
        }),
        new IAHelpStep('.filters-zone__content', this.$t('help.PEND_SORT_CHOOSE'), () => {
          this.forceOpenOptions = false;
        }),
        new IAHelpStep('.pending-plans', this.$t('help.PEND_PLANS'), null, [
          {
            text: this.$t('help.DB_ACTIONS_LINK'),
            action: () => {
              window.open(this.$router.resolve({ name: 'dmpActions' }).href, '_blank');
            },
          },
        ]),
        new IAHelpStep('.pending-action-item .drag-item', this.$t('help.PEND_PLANS_DRAG')),
      ];
    },
    makeDemoValues() {
      const mf = 'YYYY-MM-DD HH:mm:ss';
      const sd = this.$moment().subtract(1, 'H').format(mf);
      this.demoPlans = [
        {
          id: 0,
          name: this.$t('help.SHORT_TERM'),
          actionDuration: 8,
          maxActions: 20,
          support: 0,
          nActions: 2,
        },
        {
          id: 1,
          name: this.$t('help.LONG_TERM'),
          actionDuration: 72,
          maxActions: 20,
          support: 0,
          nActions: 2,
        },
        {
          id: 2,
          name: this.$t('help.SUPPORT'),
          actionDuration: 8,
          maxActions: 20,
          support: 1,
          nActions: 1,
        },
      ];
      this.demoActions = [
        {
          id: 0,
          name: this.$t('help.DEMO_ACTION1'),
          plan: 0,
          leader: null,
          pdca: 1,
          deadline: this.$moment().add(5, 'H').format(mf),
          reportTeam: 0,
          affectTeam: 0,
          added: sd,
          supportPlan: null,
          problem: 1,
          lWhat: this.$t('help.DEMO_PROBLEM1'),
          causeText: this.$t('help.DEMO_CAUSE1'),
          isSecuring: 1,
          impact: 1,
        },
        {
          id: 1,
          name: this.$t('help.DEMO_ACTION2'),
          plan: 0,
          leader: null,
          pdca: 2,
          deadline: this.$moment().add(3, 'H').format(mf),
          reportTeam: 0,
          affectTeam: 0,
          added: sd,
          supportPlan: null,
          impact: 1,
        },
      ];
    },
    /** A column header has been clicked to sort the list of problems */
    clickedSortOption(optionIdx) {
      const option = { key: 'sortBy', value: { value: optionIdx, desc: true } };
      if (this.pendOptSortBy.value === optionIdx) {
        option.value.desc = !this.pendOptSortBy.desc;
      }
      this.paramsEdit(option);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.pending-actions {
  height: 100%;
  overflow: hidden;

  &__offline-items {
    background: rgba(0, 0, 0, 0.05);
    border-radius: $main-border-radius;
    padding: .5rem;
    margin: 1rem -.5rem;

    h3 {
      margin-top: 0.2rem;
    }
  }

  &__col-headers {
    margin-top: 0.8em;
    height: 1.8em;

    & &__action {
      margin-left: 0.7rem;
      flex: 1;
      justify-content: unset;
    }

    .sort-option {
      padding: 0 0.15em;
      box-sizing: border-box;
      overflow: hidden;
      white-space: nowrap;

      &__text {
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    &__tags {
      flex: 0 0 3rem;
    }

    &__leader {
      flex: 0 0 12rem;
    }

    &__impact {
      flex: 0 0 11rem;
    }

    &__deadline {
      flex: 0 0 11rem;
    }

    &__delete {
      flex: 0 0 5.72rem;
    }
  }

  .pending-list {
    width: 71%;
    height: 100%;
    position: relative;

    .pending-action-item.ui-draggable-dragging {
      z-index: 10015;
      position: fixed !important;
      width: calc(calc(0.71 * calc(100% - calc(#{$nav-width} + #{$page-padding}))) - 1rem);
      cursor: grabbing;
      opacity: 0.9;
      box-shadow: $color-shizen-blue-btn-shadow;
    }

    .refresh-onscroll-list {
      padding-right: 1.3rem;
      padding-left: $page-content-padding;
      position: relative;
    }
  }

  &--as-widget {

    .f-h-below-title {
      height: 100%;
    }

    .pending-list {
      width: 100%;
    }
  }

  &--archives {

    .pending-list {
      width: 100%;
    }
  }

  &__main-container {
    display: flex;
    overflow: initial;
    padding: 0px;
  }

  .pending-plans {
    width: 29%;
    overflow: auto;
    height: 100%;
    position: relative;

    .plan-item {
      text-align: center;
      margin: 25px 15px;
      padding: 5px;
      box-sizing: border-box;
      transition: box-shadow 0.2s, background 0.2s;

      &.ui-droppable-active {
        padding: 2px;
        border: dashed 3px $color-shizen-blue-btn-shadow-color;
        border-radius: $main-border-radius;
      }

      &.ui-droppable-hover {
        background: $color-shizen-blue-btn-shadow-color;
      }

      &__title {
        font-size: 1.35em;
        margin-bottom: 5px;
      }

      &__help {
        margin: 1rem .3rem .5rem .3rem;
        font-size: 0.85rem;
        font-style: italic;
      }
    }
  }

  &.sm-landscape-size {

    .pending-actions {

      &__col-headers {

        &__leader {
          flex: 0 0 8rem;
        }

        &__impact {
          flex: 0 0 8rem;
        }

        &__deadline {
          flex: 0 0 6rem;
        }

        &__delete {
          flex: 0 0 4.72rem;
        }
      }
    }
  }
}

.pending-actions {

  &.sm-portrait-size {

    .pending-actions {

      &__col-headers {

        &__leader {
          flex: 0 0 4rem;
        }

        &__impact {
          flex: 0 0 4rem;
        }

        &__deadline {
          display: none;
        }
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .pending-actions {

    .pending-list {
      width: 100%;
      margin: 0;
    }

    .pending-plans {
      display: none;
    }
  }
}

</style>
